import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createEmptyCard, type Card, type Grade } from 'ts-fsrs';
import { previewIntervals } from '../lib/scheduling';
import { rateCard as rateCardServer } from '../lib/card-fns';
import {
  cardQueryOptions,
  dueCardsQueryOptions,
  scheduledCardsQueryOptions,
} from '../lib/queries';

export function useCard(
  pageNumber: number,
  isAuthenticated: boolean,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const [rated, setRated] = useState(false);

  const { data, isLoading } = useQuery({
    ...cardQueryOptions(pageNumber),
    enabled: isAuthenticated && enabled,
  });

  const card: Card | null = data?.card ?? null;

  const mutation = useMutation({
    mutationFn: (rating: Grade) =>
      rateCardServer({ data: { pageNumber, rating } }),
    onSuccess: (result) => {
      queryClient.setQueryData(cardQueryOptions(pageNumber).queryKey, result);
      void queryClient.invalidateQueries(dueCardsQueryOptions());
      void queryClient.invalidateQueries(scheduledCardsQueryOptions());
      setRated(true);
    },
  });

  const now = new Date();
  const cardForPreview: Card = card ?? createEmptyCard(now);
  const intervals = previewIntervals(cardForPreview, now);

  const rate = useCallback(
    (rating: Grade) => mutation.mutate(rating),
    [mutation],
  );

  const resetRated = useCallback(() => setRated(false), []);

  return {
    card,
    intervals,
    rate,
    isLoading,
    isRating: mutation.isPending,
    rated,
    resetRated,
  };
}
